import { Component, Input, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { CTExpansionPanelConfiguration, CtModelConfiguration, CtModelService, CTSingleComponentConfiguration, CTTabConfiguration, CtBinaryOperator, CTModelDatatableFilter, CtModelDatatableOperators } from '@ctsolution/ct-framework';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from '@ctsolution/ct-webapi';
import { CTCompany } from 'projects/ct-manager/src/app/_core/classes/ct-company';
import { CTConfigurationModel } from 'projects/ct-manager/src/app/_core/classes/ct-configuration-model';
import { LeadInformationRouteData } from 'projects/ct-manager/src/app/_core/route-data/lead-information.route-data';
import { LeadInformationFormService } from 'projects/ct-manager/src/app/components/lead/lead-edit/lead-information-form/lead-information-form.service';
import { LeadInformation } from 'projects/ct-manager/src/app/components/lead/lead-edit/lead-information-form/lead-information';
import { CompanyInformationService } from 'projects/ct-manager/src/app/components/company/company-information/company-information.service';
import { CompanyInformation } from 'projects/ct-manager/src/app/components/company/company-information/company-information';
import { CompanyInformationRouteData } from 'projects/ct-manager/src/app/_core/route-data/company-information.route-data';
import { CtManagerService } from 'projects/ct-manager/src/app/_core/lib/ct-manager.service';

@Component({
  selector: 'app-work-order-company-information',
  templateUrl: './work-order-company-information.component.html',
  styleUrls: ['./work-order-company-information.component.scss']
})
export class WorkOrderCompanyInformationComponent implements OnChanges {

  // ─── Input ──────────────────────────────────────────────────────────────────

  /** Dati azienda da mostrare in sola lettura. */
  @Input() company: CTCompany | null = null;

  /**
   * OID del Lead associato alla commessa.
   * Se valorizzato e `leadInformationEnabled = true` nella configurazione,
   * vengono caricati e mostrati i campi LeadInformation configurati
   * tramite `leadInformationFields`.
   */
  @Input() leadOid: number | null = null;

  @Input() isQuotation = false;



  // ─── Stato ──────────────────────────────────────────────────────────────────

  panelConfiguration: CTTabConfiguration | null = null;
  companyInformationConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  @ViewChild('companyInformationTemplate') companyInformationTemplate!: TemplateRef<any>;

  /**
   * Campi CompanyInformation (telefono, email, SDI ecc.) caricati dal backend.
   * Mostrati solo se la loro Description è presente in `visibleFields`.
   */
  companyContactDisplayFields: CompanyContactDisplayField[] = [];

  /**
   * Campi LeadInformation caricati dal backend per il Lead associato.
   * Ogni entry contiene la label leggibile e il valore testuale da mostrare.
   */
  leadInformationDisplayFields: LeadInformationDisplayField[] = [];

  /**
   * Flag: diventa true dopo che ngAfterViewInit ha caricato l'override di configurazione.
   * Usato per evitare che ngOnChanges tenti il caricamento prima che la configurazione
   * sia pronta, scartando erroneamente i dati per via dei valori default.
   */
  private _configurationReady = false;

     fieldConfigs: FieldConfig[] = [
    {
      key: 'ReferenceName',
      label: 'Ragione Sociale',
      getValue: (c) => c.ReferenceName
    },
    {
      key: 'DisplayName',
      label: 'Nome Visualizzato',
      getValue: (c) => c.DisplayName
    },
    {
      key: 'Name',
      label: 'Nome',
      getValue: (c) => c.Name
    },
    {
      key: 'Surname',
      label: 'Cognome',
      getValue: (c) => c.Surname
    },
    {
      key: 'FiscalCode',
      label: 'Codice Fiscale',
      getValue: (c) => c.FiscalCode
    },
    {
      key: 'VAT',
      label: 'Partita IVA',
      getValue: (c) => c.VAT
    },
    {
      key: 'VATOrFiscalCode',
      label: 'P.IVA/CF',
      getValue: (c) => c.VATOrFiscalCode
    },
    {
      key: 'Code',
      label: 'Codice',
      getValue: (c) => c.Code
    },
    {
      key: 'FullLegalAddress',
      label: 'Indirizzo Completo',
      getValue: (c) => c.FullLegalAddress
    },
    {
      key: 'Contacts',
      label: 'Contatti',
      getValue: (c) => c.Contacts
    },
    {
      key: 'ContactsInternalReferences',
      label: 'Riferimenti Interni',
      getValue: (c) => c.ContactsInternalReferences
    },
    {
      key: 'PaymentMethod',
      label: 'Modalità di Pagamento',
      getValue: (c) => c.PaymentMethod
    },
    {
      key: 'NoteContent',
      label: 'Note',
      getValue: (c) => c.NoteContent
    },
    {
      key: 'Type',
      label: 'Tipo',
      getValue: (c) => c.Type?.Name
    },
    {
      key: 'TagsJoined',
      label: 'Tags',
      getValue: (c) => c.TagsJoined
    },
    {
      key: 'IsCustomer',
      label: 'Cliente',
      getValue: (c) => c.IsCustomer ? 'Sì' : 'No'
    },
    {
      key: 'IsSupplier',
      label: 'Fornitore',
      getValue: (c) => c.IsSupplier ? 'Sì' : 'No'
    },
    {
      key: 'IsSelf',
      label: 'Azienda Propria',
      getValue: (c) => c.IsSelf ? 'Sì' : 'No'
    },
    {
      key: 'Active',
      label: 'Attivo',
      getValue: (c) => c.Active ? 'Sì' : 'No'
    }
  ]; //TODO: Usare label con traduzione



  // Campi effettivamente visibili
  displayFields: FieldConfig[] = [];


    constructor(
    private modelService: CtModelService<any>,
    private managerService: CtManagerService,
    private _webapi: CtWebapiService,
    private _leadInfoFormService: LeadInformationFormService,
    private _companyInfoService: CompanyInformationService
  ) {}

  async ngAfterViewInit(): Promise<void> {

    await this.managerService.getComponentOverride(
      'workOrder',
      'companyInformationConfiguration',
      this.companyInformationConfiguration
    );

    this.panelConfiguration = CTExpansionPanelConfiguration
      .create([
        CTSingleComponentConfiguration
          .create(this.companyInformationTemplate)
          .setLabel(this.isQuotation ? 'CompanyPotentialInformation' : 'CompanyInformation')
          .setExpanded(this.companyInformationConfiguration.expanded)
      ])
      .setButtonsVisible(false);

    this.updateDisplayFields();

    // Segna la configurazione come pronta: da questo punto ngOnChanges
    // potrà effettuare il caricamento in modo sicuro
    this._configurationReady = true;

    // Carica entrambe le sezioni con la configurazione override già applicata
    await Promise.all([
      this._loadCompanyContactFieldsIfNeeded(),
      this._loadLeadInformationIfNeeded()
    ]);
  }

  ngOnChanges(): void {
    this.updateDisplayFields();

    // Evita di eseguire il caricamento prima che ngAfterViewInit abbia applicato
    // l'override: in quel caso ci pensa ngAfterViewInit stesso al termine.
    if (!this._configurationReady) return;

    Promise.all([
      this._loadCompanyContactFieldsIfNeeded(),
      this._loadLeadInformationIfNeeded()
    ]);
  }

  // ─── Company Contact Fields ───────────────────────────────────────────

  /**
   * Carica i record CompanyInformation (telefono, email, SDI ecc.) dal backend
   * e costruisce `companyContactDisplayFields`.
   *
   * Vengono mostrati solo i tipi la cui `Description` (es. 'Phone', 'SDI')
   * è presente in `visibleFields` della configurazione.
   * La company.Oid è già disponibile tramite l'input `company`.
   */
  private async _loadCompanyContactFieldsIfNeeded(): Promise<void> {

    // Reset preventivo
    if (!this.company?.Oid) {
      this.companyContactDisplayFields = [];
      return;
    }

    try {

      // Carica i tipi dal JSON (con cache interna del service)
      const allTypes = await this._companyInfoService.getContactInformationTypes().toPromise();

      if (!allTypes?.length) return;

      const visibleFields = this.companyInformationConfiguration.visibleFields;

      // Filtra solo i tipi la cui Description compare in visibleFields
      // (il confronto è case-insensitive per robustezza)
      const enabledTypes = allTypes.filter(
        t => t.Description != null &&
          visibleFields.some(vf => vf.toLowerCase() === t.Description!.toLowerCase())
      );

      if (!enabledTypes.length) {
        this.companyContactDisplayFields = [];
        return;
      }

      // Recupera i record CompanyInformation per questa company
      const body: CtModelDatatableOperators = CtModelDatatableOperators.create().setFilters([
        CTModelDatatableFilter
          .create()
          .setField('Company.Oid')
          .setValue(this.company.Oid)
          .setOperatorType(CtBinaryOperator.Equal)
      ]);

      const request: DataRequest = new DataRequest()
        .setController([CompanyInformationRouteData().controller!])
        .setAction('List')
        .setQueryParams({ hideSpinner: true })
        .setBody(body);

      const response = await this._webapi.post<any>(request).toPromise() as CtWebapiGenericResponse<any>;
      const dataSource: CompanyInformation[] = response?.Result?.DataSource ?? [];

      // Costruisce i campi display nell'ordine di visibleFields
      // (solo quelli con un tipo corrispondente nel JSON)
      this.companyContactDisplayFields = enabledTypes
        .map(typeEntry => {

          // Trova il record salvato per questo tipo
          const existing = dataSource.find(
            el => el.Type != null && String(el.Type) === String(typeEntry.Code)
          );

          return {
            label: typeEntry.Description ?? String(typeEntry.Code),
            value: existing?.Content ?? null
          } as CompanyContactDisplayField;

        });

    } catch (err) {
      console.error('WorkOrderCompanyInformationComponent: errore caricamento CompanyInformation:', err);
      this.companyContactDisplayFields = [];
    }
  }

  // ─── Lead Information ────────────────────────────────────────────────────────

  /**
   * Carica i record LeadInformation dal backend e costruisce `leadInformationDisplayFields`.
   * Viene eseguito solo se:
   *  - `leadInformationEnabled` è true nella configurazione
   *  - `leadOid` è valorizzato
   *  - `leadInformationFields` contiene almeno un elemento
   */
  private async _loadLeadInformationIfNeeded(): Promise<void> {

    // Reset preventivo: se le condizioni non sono soddisfatte, svuotiamo la lista
    if (
      !this.companyInformationConfiguration.leadInformationEnabled ||
      !this.leadOid ||
      !this.companyInformationConfiguration.leadInformationFields?.length
    ) {
      this.leadInformationDisplayFields = [];
      return;
    }

    try {

      // Carica i tipi dal JSON (con cache interna del service)
      const allTypes = await this._leadInfoFormService.getLeadInformationTypes().toPromise();

      if (!allTypes?.length) return;

      // Recupera i codici abilitati dalla configurazione (es. [101, 102])
      const enabledCodes = this.companyInformationConfiguration.leadInformationFields
        .map(f => String(f.type));

      // Richiede i record LeadInformation per questo lead
      const body: CtModelDatatableOperators = CtModelDatatableOperators.create().setFilters([
        CTModelDatatableFilter
          .create()
          .setField('Lead.Oid')
          .setValue(this.leadOid)
          .setOperatorType(CtBinaryOperator.Equal)
      ]);

      const request: DataRequest = new DataRequest()
        .setController([LeadInformationRouteData().controller!])
        .setAction('List')
        .setQueryParams({ hideSpinner: true })
        .setBody(body);

      const response = await this._webapi.post<any>(request).toPromise() as CtWebapiGenericResponse<any>;
      const dataSource: LeadInformation[] = response?.Result?.DataSource ?? [];

      // Costruisce i campi display: solo i codici abilitati, nell'ordine di leadInformationFields
      this.leadInformationDisplayFields = this.companyInformationConfiguration.leadInformationFields
        .map(fieldConfig => {

          const code = String(fieldConfig.type);

          // Trova il tipo nel JSON per ottenere la Description (es. 'Extra1')
          const typeEntry = allTypes.find(t => String(t.Code) === code);
          if (!typeEntry) return null;

          // Trova il record salvato per questo tipo
          const existing = dataSource.find(
            el => String(el.Type) === code
          );

          return {
            // La Description del JSON (es. 'Extra1') funge da label
            label: typeEntry.Description ?? code,
            value: existing?.Content ?? null
          } as LeadInformationDisplayField;

        })
        .filter((f): f is LeadInformationDisplayField => f !== null);

    } catch (err) {
      console.error('WorkOrderCompanyInformationComponent: errore caricamento LeadInformation:', err);
      this.leadInformationDisplayFields = [];
    }
  }

  // ─── Company display fields ──────────────────────────────────────────────────

  private updateDisplayFields(): void {
    if (!this.company) {
      this.displayFields = [];
      return;
    }

    // Determina quali campi mostrare
    const fieldsToShow = this.companyInformationConfiguration.visibleFields;

    // Filtra i campi configurati in base a quelli da mostrare
    this.displayFields = this.fieldConfigs.filter(config =>
      fieldsToShow.includes(config.key)
    );

    // Ordina i campi secondo l'ordine nell'array visibleFields o defaultFields
    this.displayFields.sort((a, b) => {
      const indexA = fieldsToShow.indexOf(a.key);
      const indexB = fieldsToShow.indexOf(b.key);
      return indexA - indexB;
    });
  }

  // Metodo helper per verificare se un valore è vuoto
  isEmpty(value: any): boolean {
    return value === null ||
           value === undefined ||
           value === '' ||
           (typeof value === 'string' && value.trim() === '');
  }

  // Metodo per ottenere il valore formattato di un campo
  getFieldValue(field: FieldConfig): string {
    if (!this.company) return '-';

    const value = field.getValue(this.company);

    if (this.isEmpty(value)) {
      return '-';
    }

    return value;
  }

  // Metodo per verificare se ci sono dati company da mostrare
  hasDataToShow(): boolean {
    return this.displayFields.length > 0 && this.company !== null;
  }

  /** Restituisce true se ci sono campi CompanyInformation (contatti) da mostrare */
  hasCompanyContactDataToShow(): boolean {
    return this.companyContactDisplayFields.length > 0;
  }

  /** Restituisce true se ci sono campi LeadInformation da mostrare */
  hasLeadDataToShow(): boolean {
    return this.leadInformationDisplayFields.length > 0;
  }

}

interface FieldConfig {
  key: string;
  label: string;
  getValue: (company: CTCompany) => any;
  isVisible?: boolean;
}

/** Campo CompanyInformation (contatti: telefono, email, SDI ecc.) in sola lettura. */
interface CompanyContactDisplayField {
  /** Description del JSON dei tipi (es. 'Phone', 'Email', 'SDI'). */
  label: string;
  /** Valore testuale salvato sul backend, null se non ancora inserito. */
  value: string | null;
}

/** Campo LeadInformation in sola lettura da mostrare nella sezione company-information. */
interface LeadInformationDisplayField {
  /** Description del JSON dei tipi (es. 'Extra1', 'Extra2'). */
  label: string;
  /** Valore testuale salvato sul backend, null se non ancora inserito. */
  value: string | null;
}
